import { logEvent } from 'firebase/analytics';
import { analytics } from './firebase';
import { getCurrentOrgName } from './helpers';
import { getWebappVersion } from './get-build-version';

const isProduction = ['PROD', 'PREPROD'].includes(process.env.NEXT_PUBLIC_HOME_REMOTE_CONFIG_ENV);
/**
 * Checks if we're running on client side
 * @returns {boolean}
 */
const isClient = () => typeof window !== 'undefined';

let clevertap;

/**
 * Initialize CleverTap SDK
 * @param {string} accountId - CleverTap account ID
 */
export const initializeAnalytics = async () => {
  if (!isClient()) return;

  try {
    // Initialize CleverTap
    const module = await import('clevertap-web-sdk');
    clevertap = module.default;
    const orgName = getCurrentOrgName();
    let CLEVERTAP_ACCOUNT_ID = process.env.NEXT_PUBLIC_NEXTIAS_CLEVERTAP_ACCOUNT_ID;
    if (orgName === 'nextias') {
      CLEVERTAP_ACCOUNT_ID = process.env.NEXT_PUBLIC_NEXTIAS_CLEVERTAP_ACCOUNT_ID;
    } else if (orgName === 'madeeasyprime') {
      CLEVERTAP_ACCOUNT_ID = process.env.NEXT_PUBLIC_MADEEASY_CLEVERTAP_ACCOUNT_ID;
    }

    if (!CLEVERTAP_ACCOUNT_ID) {
      throw new Error('CleverTap Account ID is not configured');
    }

    clevertap.privacy.push({ optOut: false });
    clevertap.privacy.push({ useIP: true });
    clevertap.init(CLEVERTAP_ACCOUNT_ID);
    const clevertapLogLevel = isProduction ? 1 : 3;
    clevertap.setLogLevel(clevertapLogLevel); // Set log level to debug for development

    // Return the initialized instance
    // return clevertap;
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Failed to initialize CleverTap:', error);
    }
  }
};

/**
 * Generic event tracking function that sends events to both Firebase and CleverTap
 * @param {string} eventName - Name of the event
 * @param {Object} eventProperties - Properties/parameters for the event
 */

let currentUser = {};

export const setAnalyticsUser = (user) => {
  currentUser = {
    userName: user?.name,
    userEmail: user?.email,
    accountId: user?.accountId || user?.id || user?.profile?.accountDetails?.accountId,
    phone: user?.phone,
  };
};

export const trackEvent = async (eventName, eventProperties = {}) => {
  if (!isClient()) return; // Skip if running on server
  try {
    const updatedProperties = {
      ...eventProperties,
      ...currentUser,
      deviceType: 'web',
      coursesPrepBuildVersion: getWebappVersion() || 'unknown',
    };
    // Send to Firebase (analytics will be undefined on server)
    if (analytics && !isProduction) {
      logEvent(analytics, eventName, updatedProperties);
    }

    // Send to CleverTap
    if (clevertap?.event) {
      clevertap.event.push(eventName, updatedProperties);
    }
  } catch (error) {
    // Silently handle error in production
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error tracking event:', error);
    }
  }
};

/**
 * Track user profile updates
 * @param {Object} userProperties - User properties to update
 */
export const trackUserProfile = async (userProperties) => {
  if (!isClient()) return; // Skip if running on server

  try {
    // Update CleverTap user profile
    if (clevertap?.profile) {
      clevertap.profile.push({
        Site: {
          ...userProperties,
        },
      });
    }

    // Set user properties in Firebase
    if (analytics && !isProduction) {
      logEvent(analytics, 'user_profile_update', userProperties);
    }
  } catch (error) {
    // Silently handle error in production
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating user profile:', error);
    }
  }
};

/**
 * Track user login and identify user in analytics platforms
 * @param {Object}userData - Any additional properties to track
 */
export const onUserLogin = async (userData) => {
  if (!isClient()) return;

  const userProperties = {
    Name: userData?.name,
    Identity: userData?.accountId,
    Email: userData?.email,
    Phone: `+91${userData?.phone}`,
    Gender: userData?.gender,
  };

  try {
    // const userProfile = {
    //   ...userProperties,
    // };

    // Set user properties in CleverTap
    if (clevertap?.onUserLogin) {
      clevertap.onUserLogin.push({
        Site: userProperties,
      });
    }

    // Set user ID in Firebase Analytics
    // if (analytics) {
    //   logEvent(analytics, 'login', {
    //     user_id: userData.id,
    //     method: additionalProperties.method || 'email',
    //   });
    //   // Track additional user properties
    //   logEvent(analytics, 'set_user_properties', userProfile);
    // }
  } catch (error) {
    // Silently handle error in production
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error tracking user login:', error);
    }
  }
};

export const updateCleverTapProfile = (user) => {
  if (!isClient()) return;
  try {
    if (clevertap?.profile) {
      clevertap.profile.push({
        Site: {
          Name: user?.name,
          Identity: user?.id || user?.profile?.accountDetails?.accountId,
          Email: user?.email,
          Phone: `+91${user?.phone}`,
          Gender: user?.profile?.accountDetails?.gender,
        },
      });
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    if (process.env.NODE_ENV === 'development') {
      console.error('Error updating CleverTap profile:', error);
    }
  }
};

/**
 * Common event names to maintain consistency
 */
export const EventNames = {
  PAGE_VIEW: 'page_view',
  BUTTON_CLICK: 'button_click',
  FORM_SUBMIT: 'form_submit',
  VIDEO_START: 'video_start',
  VIDEO_COMPLETE: 'video_complete',
  COURSE_VIEW: 'course_view',
  COURSE_ENROLL: 'course_enroll',
  TEST_START: 'test_start',
  TEST_COMPLETE: 'test_complete',
  ERROR_OCCURRED: 'error_occurred',
  LOGIN_SUCCESS: 'login_success',
  LOGIN_FAILURE: 'login_failure',
  SIGNUP_SUCCESS: 'signup_success',
  SIGNUP_FAILURE: 'signup_failure',
};

/**
 * Pre-defined event properties to maintain consistency
 */
export const EventProperties = {
  COURSE: {
    COURSE_ID: 'course_id',
    COURSE_NAME: 'course_name',
    COURSE_CATEGORY: 'course_category',
    COURSE_PRICE: 'course_price',
  },
  TEST: {
    TEST_ID: 'test_id',
    TEST_NAME: 'test_name',
    TEST_TYPE: 'test_type',
    SCORE: 'score',
  },
  USER: {
    USER_ID: 'user_id',
    USER_TYPE: 'user_type',
    USER_ROLE: 'user_role',
    SUBSCRIPTION_STATUS: 'subscription_status',
  },
  INTERACTION: {
    BUTTON_ID: 'button_id',
    BUTTON_NAME: 'button_name',
    PAGE_NAME: 'page_name',
    SECTION_NAME: 'section_name',
    ACTION_TYPE: 'action_type',
  },
};
